import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { AuthProvider, useAuth, authFetch } from './context/AuthContext';
import LoginPage from './components/LoginPage';
import BotStatus from './components/BotStatus';
import AiChatPanel from './components/AiChatPanel';
import StatsGrid from './components/StatsGrid';
import UsersTable from './components/UsersTable';
import TransactionsTable from './components/TransactionsTable';
import ActivityLog from './components/ActivityLog';
import FeeSchedule from './components/FeeSchedule';
import AnalyticsCharts from './components/AnalyticsCharts';
import ExportButtons from './components/ExportButtons';
import AdminManagement from './components/AdminManagement';
import EmailSettings from './components/EmailSettings';
import VerifyPage from './components/VerifyPage';
import LandingPage from './components/LandingPage';
import KycPage from './components/KycPage';
import WebSignupPage from './components/WebSignupPage';
import PrivateView from './components/PrivateView';
import { TFMark } from './components/TFLogo';
import { List, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [botStatus, setBotStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, txnRes, botRes] = await Promise.all([
        authFetch(`${API}/api/stats`), authFetch(`${API}/api/users`),
        authFetch(`${API}/api/transactions`), authFetch(`${API}/api/bot/status`),
      ]);
      if (statsRes.status === 401) { window.location.reload(); return; }
      const [statsData, usersData, txnData, botData] = await Promise.all([
        statsRes.json(), usersRes.json(), txnRes.json(), botRes.json(),
      ]);
      setStats(statsData); setUsers(usersData.users || []);
      setTransactions(txnData.transactions || []); setBotStatus(botData);
    } catch (err) { console.error('Fetch error:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';
    let ws = null; let reconnectTimer = null;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => { setWsConnected(true); };
      ws.onmessage = (e) => { try { const m = JSON.parse(e.data); if (m.type === 'data_update' || m.type === 'bot_event') fetchData(); } catch {} };
      ws.onclose = () => { setWsConnected(false); reconnectTimer = setTimeout(connect, 5000); };
      ws.onerror = () => { ws.close(); };
    };
    connect();
    const interval = setInterval(fetchData, 30000);
    return () => { clearInterval(interval); if (reconnectTimer) clearTimeout(reconnectTimer); if (ws) ws.close(); };
  }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border border-tf-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-mono text-tf-text-2 text-[10px] tracking-[0.3em] uppercase">Loading TrustFrame</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="dashboard">
      {/* Header */}
      <header className="bg-tf-surface border-b border-tf-primary px-4 sm:px-6 lg:px-10 py-3 sm:py-4" data-testid="header">
        {/* Top row: Logo + mobile menu toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <TFMark color="#002FA7" size={26} className="flex-shrink-0" />
            <div>
              <h1
                className="font-heading font-bold text-base sm:text-lg uppercase text-tf-primary"
                style={{ letterSpacing: '0.85em', paddingLeft: '0.85em' }}
              >
                TRUSTFRAME
              </h1>
              <p className="font-mono text-[8px] sm:text-[9px] tracking-[0.1em] sm:tracking-[0.15em] text-tf-text-2 uppercase hidden sm:block mt-0.5">[ .infrastructure. .platform. ]</p>
            </div>
          </div>

          {/* Desktop controls */}
          <div className="hidden lg:flex items-center gap-4">
            <BotStatus status={botStatus} wsConnected={wsConnected} />
            <ExportButtons />
            <button data-testid="refresh-btn" onClick={fetchData}
              className="px-4 py-2 border border-tf-primary text-tf-primary font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-tf-primary hover:text-white transition-all duration-300">Refresh</button>
            <div className="flex items-center gap-3 pl-4 border-l border-tf-border-light">
              <span className={`inline-block px-2 py-0.5 font-mono text-[9px] tracking-[0.15em] uppercase border ${
                user?.role === 'super_admin' ? 'border-tf-accent text-tf-accent' : user?.role === 'admin' ? 'border-tf-primary text-tf-primary' : 'border-tf-text-2 text-tf-text-2'
              }`} data-testid="admin-role-badge">{user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'Viewer'}</span>
              <span className="font-mono text-[10px] text-tf-text-2" data-testid="admin-email">{user?.email}</span>
              <button data-testid="logout-btn" onClick={logout}
                className="px-3 py-1.5 border border-tf-danger text-tf-danger font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-tf-danger hover:text-white transition-all duration-300">Logout</button>
            </div>
          </div>

          {/* Mobile: status dots + menu toggle */}
          <div className="flex lg:hidden items-center gap-3">
            <BotStatus status={botStatus} wsConnected={wsConnected} compact />
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 border border-tf-primary text-tf-primary" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-3 pt-3 border-t border-tf-border-muted space-y-3">
            <div className="flex items-center justify-between">
              <span className={`inline-block px-2 py-0.5 font-mono text-[9px] tracking-[0.15em] uppercase border ${
                user?.role === 'super_admin' ? 'border-tf-accent text-tf-accent' : user?.role === 'admin' ? 'border-tf-primary text-tf-primary' : 'border-tf-text-2 text-tf-text-2'
              }`} data-testid="admin-role-badge-mobile">{user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'Viewer'}</span>
              <span className="font-mono text-[10px] text-tf-text-2">{user?.email}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <ExportButtons />
              <button data-testid="refresh-btn-mobile" onClick={() => { fetchData(); setMobileMenuOpen(false); }}
                className="px-3 py-1.5 border border-tf-primary text-tf-primary font-mono text-[9px] tracking-[0.15em] uppercase">Refresh</button>
              <button onClick={logout}
                className="px-3 py-1.5 border border-tf-danger text-tf-danger font-mono text-[9px] tracking-[0.15em] uppercase">Logout</button>
            </div>
          </div>
        )}
      </header>

      {/* Main */}
      <main className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8">
        <div className="animate-fade-in stagger-1"><StatsGrid stats={stats} /></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6 lg:mt-8">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <div className="animate-fade-in stagger-2"><UsersTable users={users} /></div>
            <div className="animate-fade-in stagger-3"><TransactionsTable transactions={transactions} onRefresh={fetchData} /></div>
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <div className="animate-fade-in stagger-4"><AdminManagement /></div>
            )}
          </div>
          <div className="space-y-4 sm:space-y-6">
            <div className="animate-fade-in stagger-2"><AnalyticsCharts /></div>
            <div className="animate-fade-in stagger-3"><ActivityLog logs={stats?.recent_activity || []} /></div>
            <div className="animate-fade-in stagger-4"><FeeSchedule /></div>
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <div className="animate-fade-in stagger-4"><EmailSettings /></div>
            )}
          </div>
        </div>
      </main>
      <AiChatPanel
        surface="admin"
        storageKey="tf_admin_ai_session"
        title="TrustFrame Ops Copilot"
        theme={{
          bg: '#FFFFFF',
          surface: '#F5F4F0',
          text: '#1A1A1A',
          textMuted: '#5a5a66',
          border: '#002FA7',
          accent: '#002FA7',
          accentInk: '#ffffff',
        }}
        suggestions={[
          'Summarise the top 5 open transactions.',
          'Draft a reply to a collector asking about escrow release timelines.',
          'What KYC edge cases should I flag on a corporate applicant?',
        ]}
      />
    </div>
  );
}

function VerifyRoute() { const { certId } = useParams(); return <VerifyPage certId={certId} />; }

function AppContent() {
  const { user, checking } = useAuth();
  if (checking) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border border-tf-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-mono text-tf-text-2 text-[10px] tracking-[0.3em] uppercase">Loading TrustFrame</p>
      </div>
    </div>
  );
  if (!user) return <LoginPage />;
  return <Dashboard />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<WebSignupPage />} />
          <Route path="/kyc/:externalUserId" element={<KycPage />} />
          <Route path="/dashboard" element={<AppContent />} />
          <Route path="/verify/:certId" element={<VerifyRoute />} />
          <Route path="/private" element={<PrivateView />} />
          <Route path="/private/:section" element={<PrivateView />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
